"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { 
  AlertCircle,
  Archive,
  Clock,
  MessageSquare, 
  MoreVertical, 
  Phone, 
  Plus,
  Search, 
  Send, 
  Star,
  Trash2,
  User, 
  VideoIcon,
} from 'lucide-react';
import React, { useState } from 'react';

const ChatPage: React.FC = () => {
  const [selectedChat, setSelectedChat] = useState<number | null>(1);

  const chats = [
    {
      id: 1,
      name: "Іван Петренко",
      lastMessage: "Добре, дякую за інформацію",
      time: "14:23",
      unread: 2,
      avatar: "https://picsum.photos/seed/user1/100/100",
      status: "online",
      email: "ivan.petrenko@example.com",
    },
    {
      id: 2,
      name: "Марія Коваленко",
      lastMessage: "Коли можна оглянути квартиру?",
      time: "12:45",
      unread: 0,
      avatar: "https://picsum.photos/seed/user2/100/100",
      status: "offline",
      email: "maria.kovalenko@example.com",
    },
    {
      id: 3,
      name: "Олександр Ковальчук",
      lastMessage: "Дякую за швидку відповідь",
      time: "10:30",
      unread: 1,
      avatar: "https://picsum.photos/seed/user3/100/100",
      status: "online",
      email: "oleksandr.kovalchuk@example.com",
    },
    {
      id: 4,
      name: "Наталія Шевченко",
      lastMessage: "Можемо зустрітись завтра о 15:00",
      time: "Вчора",
      unread: 0,
      avatar: "https://picsum.photos/seed/user4/100/100",
      status: "offline",
      email: "natalia.shevchenko@example.com",
    },
    {
      id: 5,
      name: "Андрій Мельник",
      lastMessage: "Надішліть, будь ласка, фото квартири",
      time: "Вчора",
      unread: 3,
      avatar: "https://picsum.photos/seed/user5/100/100",
      status: "online",
      email: "andriy.melnyk@example.com",
    },
    {
      id: 6,
      name: "Ольга Лисенко",
      lastMessage: "Дякую за допомогу!",
      time: "2 дні тому",
      unread: 0,
      avatar: "https://picsum.photos/seed/user6/100/100",
      status: "offline",
      email: "olga.lysenko@example.com",
    },
    {
      id: 7,
      name: "Василь Коваль",
      lastMessage: "Чи є знижка при довгостроковій оренді?",
      time: "3 дні тому",
      unread: 0,
      avatar: "https://picsum.photos/seed/user7/100/100",
      status: "offline",
      email: "vasyl.koval@example.com",
    }
  ];

  const messages = [
    {
      id: 1,
      sender: "Іван Петренко",
      content: "Доброго дня! Маю питання щодо оренди квартири.",
      time: "14:20",
      isOwn: false,
    },
    {
      id: 2,
      sender: "Ви",
      content: "Доброго дня! Так, слухаю вас.",
      time: "14:21",
      isOwn: true,
    },
    {
      id: 3,
      sender: "Іван Петренко",
      content: "Чи можливо подивитися квартиру сьогодні ввечері?",
      time: "14:22",
      isOwn: false,
    },
    {
      id: 4,
      sender: "Ви",
      content: "На жаль, сьогодні ввечері квартира зайнята. Можемо організувати перегляд завтра о 12:00 або 15:00.",
      time: "14:23",
      isOwn: true,
    },
    {
      id: 5,
      sender: "Іван Петренко",
      content: "Добре, давайте завтра о 15:00.",
      time: "14:24",
      isOwn: false,
    },
    {
      id: 6,
      sender: "Ви",
      content: "Чудово! Адреса: вул. Шевченка, 10, кв. 5. Я буду чекати вас біля під'їзду.",
      time: "14:25",
      isOwn: true,
    },
    {
      id: 7,
      sender: "Іван Петренко",
      content: "Дякую! А які умови оренди?",
      time: "14:26",
      isOwn: false,
    },
    {
      id: 8,
      sender: "Ви",
      content: "Оренда складає 12000 грн/міс + комунальні послуги. Необхідна застава в розмірі місячної оренди. Мінімальний термін - 6 місяців.",
      time: "14:27",
      isOwn: true,
    },
    {
      id: 9,
      sender: "Іван Петренко",
      content: "Зрозуміло. А які меблі є в квартирі?",
      time: "14:28",
      isOwn: false,
    },
    {
      id: 10,
      sender: "Ви",
      content: "Квартира повністю мебльована: кухонний гарнітур, холодильник, пральна машина, диван, шафа, двоспальне ліжко, телевізор.",
      time: "14:29",
      isOwn: true,
    },
    {
      id: 11,
      sender: "Іван Петренко",
      content: "Чудово! Тоді до завтра!",
      time: "14:30",
      isOwn: false,
    },
    {
      id: 12,
      sender: "Ви",
      content: "До зустрічі! Якщо виникнуть питання - пишіть.",
      time: "14:31",
      isOwn: true,
    }
  ];

  const selectedChatData = chats.find(chat => chat.id === selectedChat);

  return (
    <TooltipProvider delayDuration={0}>
      <div className="h-screen">
        <div className="h-full p-4">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-primary/10 rounded-full">
                <MessageSquare className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Управління чатами</h1>
                <p className="text-muted-foreground">Спілкування з орендарями та власниками</p>
              </div>
            </div>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Створити чат
            </Button>
          </div>
          <Card className="h-[calc(100vh-7rem)]">
            <div className="flex h-full">
              {/* Sidebar */}
              <div className="w-[300px] border-r flex flex-col">
                <div className="p-4 border-b">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Пошук чату..." className="pl-8" />
                  </div>
                </div>
                <ScrollArea className="flex-1">
                  <div className="p-2 space-y-2">
                    {chats.map((chat) => (
                      <div
                        key={chat.id}
                        onClick={() => setSelectedChat(chat.id)}
                        className={cn(
                          "flex items-center gap-3 rounded-lg p-2 cursor-pointer",
                          selectedChat === chat.id ? "bg-accent" : "hover:bg-accent/50",
                          "relative group"
                        )}
                      >
                        <div className="relative">
                          <Avatar>
                            <AvatarImage src={chat.avatar} />
                            <AvatarFallback>
                              <User className="h-4 w-4" />
                            </AvatarFallback>
                          </Avatar>
                          <div className={cn(
                            "absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-background",
                            chat.status === "online" ? "bg-green-500" : "bg-gray-400"
                          )} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium truncate text-sm">{chat.name}</p>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {chat.time}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {chat.lastMessage}
                          </p>
                        </div>
                        {chat.unread > 0 && (
                          <Badge variant="default" className="ml-auto">
                            {chat.unread}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              {/* Chat Area */}
              {selectedChatData ? (
                <div className="flex-1 flex flex-col min-w-0">
                  <div className="flex items-center justify-between p-4 border-b">
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={selectedChatData.avatar} />
                        <AvatarFallback>
                          <User className="h-4 w-4" />
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h2 className="text-sm font-semibold">{selectedChatData.name}</h2>
                        <p className="text-xs text-muted-foreground">{selectedChatData.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <Phone className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Аудіо дзвінок</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <VideoIcon className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Відео дзвінок</TooltipContent>
                      </Tooltip>
                      <Separator orientation="vertical" className="h-6" />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Star className="mr-2 h-4 w-4" /> Додати до важливих
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Archive className="mr-2 h-4 w-4" /> Архівувати чат
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-600">
                            <Trash2 className="mr-2 h-4 w-4" /> Видалити чат
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <ScrollArea className="flex-1 px-4">
                    <div className="py-4 space-y-4">
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          className={cn(
                            "flex",
                            message.isOwn ? "justify-end" : "justify-start"
                          )}
                        >
                          <div
                            className={cn(
                              "max-w-[70%] rounded-lg p-3",
                              message.isOwn
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted"
                            )}
                          >
                            <p className="text-sm break-words">{message.content}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs opacity-70">{message.time}</span>
                              {message.isOwn && (
                                <AlertCircle className="h-3 w-3 text-primary-foreground/70" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                  <div className="p-4 border-t">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Введіть повідомлення..."
                        className="flex-1"
                      />
                      <Button>
                        <Send className="h-4 w-4 mr-2" />
                        Надіслати
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground" />
                    <h3 className="mt-4 text-lg font-medium">Оберіть чат</h3>
                    <p className="text-sm text-muted-foreground">
                      Оберіть чат зі списку зліва, щоб почати спілкування
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default ChatPage; 